import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { identitySwitchPortal } from '@/store/identitySlice';
import { PORTAL_BRANDING } from '@/config/portalConfig';
import { portalCodeToPath } from '@/config/portalConfig';
import type { PortalCode } from '@/types/identity';
import { showApiError } from '@/utils/apiError';
import { Select } from '@/components/ui/Select';
export function PortalSwitcher() {
  const { t } = useTranslation('identity');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useAppSelector((s) => s.identity.session);
  const [switching, setSwitching] = useState(false);

  if (!session || session.availablePortals.length <= 1) {
    return null;
  }

  const uniquePortals = session.availablePortals.reduce(
    (acc, p) => {
      const key = `${p.profileId}-${p.portalCode}`;
      if (!acc.some((x) => `${x.profileId}-${x.portalCode}` === key)) {
        acc.push(p);
      }
      return acc;
    },
    [] as typeof session.availablePortals,
  );

  const handleSwitch = async (profileId: number, portalCode: PortalCode) => {
    if (profileId === session.activeProfileId && portalCode === session.activePortalCode) {
      return;
    }
    setSwitching(true);
    try {
      await dispatch(identitySwitchPortal({ profileId, portalCode })).unwrap();
      navigate(portalCodeToPath(portalCode), { replace: true });
    } catch (err) {
      showApiError(err, t('errors.couldNotSwitchPortal'));
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Select
      label={t('labels.switchPortal')}
      disabled={switching}
      required
      allowClear={false}
      value={`${session.activeProfileId}:${session.activePortalCode}`}
      options={uniquePortals.map((p) => ({
        value: `${p.profileId}:${p.portalCode}`,
        label: `${PORTAL_BRANDING[p.portalCode as PortalCode]?.title ?? p.portalCode}${p.organizationName ? ` — ${p.organizationName}` : ''}`,
      }))}
      onChange={(next) => {
        const [profileId, portalCode] = next.split(':');
        void handleSwitch(Number(profileId), portalCode as PortalCode);
      }}
    />
  );}
